import { useState, useMemo, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { Accordion } from '@/components/ui/Accordion'
import { ScentFamilyBadge } from '@/components/fragrance/ScentFamilyBadge'
import { NotesPyramid } from '@/components/fragrance/NotesPyramid'
import { SizePriceSelector } from '@/components/fragrance/SizePriceSelector'
import { ProductGrid } from '@/components/sections/ProductGrid'
import { ImageGallery } from '@/components/product/ImageGallery'
import { AddToBagConfirmation } from '@/components/product/AddToBagConfirmation'
import { ReviewsSection } from '@/components/product/ReviewsSection'
import { useProduct } from '@/hooks/useProduct'

import type { Perfume, ScentFamily } from '@/types'
import { useCartStore } from '@/stores/cartStore'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { useScentProfileStore } from '@/stores/scentProfileStore'
import { useClickOutside } from '@/hooks/useClickOutside'
import { formatPrice, cn } from '@/lib/utils'
import {
  buyNow,
  estimateDelivery,
  addToWishlist,
  removeFromWishlist,
  fetchWishlist,
  getRelatedProducts,
  type DeliveryEstimate,
  type SearchResult,
} from '@/lib/api'
import { Input } from '@/components/ui/Input'
import { WishlistHeart } from '@/components/ui/WishlistHeart'
import { Star, Leaf, X, MapPin, Clock, Info } from 'lucide-react'

const RECENTLY_VIEWED_KEY = 'little-sky-recently-viewed'
const MAX_RECENT = 8

interface RecentItem {
  slug: string
  name: string
  family: string
  image: string
  price: number
}

function loadRecent(): RecentItem[] {
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_KEY)
    return raw ? (JSON.parse(raw) as RecentItem[]) : []
  } catch {
    return []
  }
}

function saveRecent(perfume: Perfume, price: number) {
  if (!perfume?.id) return
  const next = [
    {
      slug: perfume.id,
      name: perfume.name,
      family: perfume.family,
      image: perfume.images[0] ?? '',
      price,
    },
    ...loadRecent().filter((s) => s.slug !== perfume.id),
  ].slice(0, MAX_RECENT)
  try {
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next))
  } catch {}
}

export function ProductDetail() {
  const { id } = useParams()
  const { product, loading } = useProduct(id)
  const perfume = product
  const addItem = useCartStore((s) => s.addItem)
  const addToast = useUIStore((s) => s.addToast)
  const pulseBag = useUIStore((s) => s.pulseBag)

  const [selectedSize, setSelectedSize] = useState(perfume?.sizes[0]?.id ?? '')
  const [addedOpen, setAddedOpen] = useState(false)
  const [buyNowOpen, setBuyNowOpen] = useState(false)
  const buyNowRef = useRef<HTMLDivElement>(null)
  useClickOutside(buyNowRef, () => setBuyNowOpen(false), buyNowOpen)
  const [buyNowLoading, setBuyNowLoading] = useState(false)
  const [buyNowForm, setBuyNowForm] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
  })
  const [deliveryState, setDeliveryState] = useState('')
  const [deliveryCity, setDeliveryCity] = useState('')
  const [deliveryEstimate, setDeliveryEstimate] = useState<DeliveryEstimate | null>(null)
  const [deliveryLoading, setDeliveryLoading] = useState(false)
  const [related, setRelated] = useState<SearchResult[]>([])
  const recentlyViewed = useMemo(() => loadRecent().filter((r) => r.slug !== perfume?.id), [perfume?.id])

  const user = useAuthStore((s) => s.user)
  const { wishlistIds, toggleWishlist, setWishlistIds } = useScentProfileStore()
  const isWishlisted = wishlistIds.includes(perfume?.id ?? '')
  const [wishlistLoading, setWishlistLoading] = useState(false)
  const [wishlistBurst, setWishlistBurst] = useState(0)

  // Re-select whenever the size list itself changes. The static catalogue
  // renders first and the API's real variant ids replace it on load, so keying
  // this on the product id alone left a selection pointing at an id that no
  // longer existed — which made Add to cart a silent no-op.
  const firstSizeId = perfume?.sizes[0]?.id ?? ''
  useEffect(() => {
    if (firstSizeId) setSelectedSize(firstSizeId)
  }, [firstSizeId])

  useEffect(() => {
    if (!user) return
    let cancelled = false
    fetchWishlist().then((slugs) => {
      if (!cancelled) setWishlistIds(slugs)
    })
    return () => {
      cancelled = true
    }
  }, [user, setWishlistIds])

  useEffect(() => {
    if (user?.email && !buyNowForm.email) {
      setBuyNowForm((f) => ({ ...f, email: user.email }))
    }
  }, [user?.email, buyNowForm.email])

  // Persist recently viewed products for the "recently viewed" rail.
  useEffect(() => {
    if (!perfume?.id) return
    const price = perfume.sizes.find((s) => s.id === selectedSize)?.price ?? perfume.sizes[0]?.price ?? 0
    saveRecent(perfume, price)
  }, [perfume, selectedSize])

  useEffect(() => {
    if (!perfume?.id) return
    let cancelled = false
    getRelatedProducts(perfume.id, 4).then((items) => {
      if (!cancelled) setRelated(items)
    })
    return () => {
      cancelled = true
    }
  }, [perfume?.id])

  if (loading) {
    return (
      <Container className="py-32 text-center">
        <p className="text-carbon/60">Loading fragrance...</p>
      </Container>
    )
  }

  if (!perfume) {
    return (
      <Container className="py-32 text-center">
        <h1 className="font-display text-3xl text-carbon">Fragrance not found</h1>
        <Link to="/shop" className="mt-4 inline-block text-carbon hover:underline">
          Back to shop
        </Link>
      </Container>
    )
  }

  const selectedSizeObj = perfume.sizes.find((s) => s.id === selectedSize) ?? perfume.sizes[0]
  if (!selectedSizeObj) {
    return (
      <Container className="py-32 text-center">
        <h1 className="font-display text-3xl text-carbon">Fragrance unavailable</h1>
        <p className="mt-2 text-carbon/60">No sizes are configured for this product.</p>
      </Container>
    )
  }

  const sizeOutOfStock = selectedSizeObj?.inStock === false

  const handleAdd = () => {
    if (!selectedSizeObj) return
    addItem(perfume, selectedSizeObj.id)
    pulseBag()
    setAddedOpen(true)
  }

  const handleSample = () => {
    const sample = perfume.sizes.find((s) => s.size.includes('sample'))
    if (sample) {
      addItem(perfume, sample.id)
      pulseBag()
      setAddedOpen(true)
    }
  }

  const fetchDeliveryEstimate = async () => {
    if (!deliveryState.trim()) {
      setDeliveryEstimate(null)
      return
    }
    setDeliveryLoading(true)
    try {
      const estimate = await estimateDelivery({ state: deliveryState.trim(), city: deliveryCity.trim() })
      setDeliveryEstimate(estimate)
    } catch {
      setDeliveryEstimate(null)
    } finally {
      setDeliveryLoading(false)
    }
  }

  const handleBuyNow = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!perfume.apiId || !selectedSizeObj) return
    setBuyNowLoading(true)
    try {
      const result = await buyNow({
        email: buyNowForm.email,
        name: buyNowForm.name || undefined,
        phone: buyNowForm.phone || undefined,
        address: {
          street: buyNowForm.street,
          city: buyNowForm.city,
          state: buyNowForm.state,
          country: 'NG',
        },
        productId: perfume.apiId,
        variantId: Number(selectedSizeObj.id),
        quantity: 1,
        callbackUrl: `${window.location.origin}/order-status`,
      })
      if (!result.success) {
        addToast(result.error || 'Could not start checkout.', 'error')
        setBuyNowLoading(false)
        return
      }
      window.location.href = result.authorizationUrl
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Could not start checkout.', 'error')
      setBuyNowLoading(false)
    }
  }

  const handleWishlist = async () => {
    if (!perfume) return
    const previous = wishlistIds
    toggleWishlist(perfume.id)
    if (!isWishlisted) setWishlistBurst((n) => n + 1)
    setWishlistLoading(true)
    try {
      const slugs = isWishlisted ? await removeFromWishlist(perfume.id) : await addToWishlist(perfume.id)
      setWishlistIds(slugs)
      addToast(
        isWishlisted ? `Removed ${perfume.name} from wishlist` : `Added ${perfume.name} to wishlist`,
        'success'
      )
    } catch {
      setWishlistIds(previous)
      addToast('Could not update your wishlist. Please try again.', 'error')
    } finally {
      setWishlistLoading(false)
    }
  }

  const accordions = [
    {
      id: 'story',
      title: 'The story',
      content: <p>{perfume.story}</p>,
    },
    {
      id: 'notes',
      title: 'Notes pyramid',
      content: <NotesPyramid notes={perfume.notes} />,
    },
    {
      id: 'details',
      title: 'Fragrance details',
      content: (
        <div className="space-y-2 text-sm text-carbon/70">
          <p>Concentration: {perfume.concentration}</p>
          {!!perfume.longevityHoursMin && !!perfume.longevityHoursMax && (
            <p>Longevity: {perfume.longevityHoursMin}–{perfume.longevityHoursMax} hours</p>
          )}
          {perfume.smellsLike && perfume.smellsLike.length > 0 && (
            <p>Smells like: {perfume.smellsLike.join(', ')}</p>
          )}
          {perfume.occasionTags && perfume.occasionTags.length > 0 && (
            <p>Occasions: {perfume.occasionTags.join(', ')}</p>
          )}
        </div>
      ),
    },
    {
      id: 'ingredients',
      title: 'Ingredients & allergens',
      content: (
        <p>
          Alcohol Denat., Parfum (Fragrance), Aqua (Water), and the notes listed above.
          Contains natural extracts and synthetic molecules selected for safety and longevity.
          Patch test if sensitive.
        </p>
      ),
    },
    {
      id: 'sustainability',
      title: 'Sustainability',
      content: (
        <div className="space-y-2">
          <p>Bottle is recyclable glass. Outer carton is FSC-certified. 50ml and 100ml sizes are refillable.</p>
          <div className="flex items-center gap-2 text-sm">
            <Leaf className="h-4 w-4 text-wd-orange" />
            <span>Cruelty-free. No animal testing.</span>
          </div>
        </div>
      ),
    },
  ]

  return (
    <div className="pt-28 pb-24">
      <Container>
        <div className="grid gap-12 lg:grid-cols-2">
          <ImageGallery images={perfume.images} productName={perfume.name} />

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col lg:sticky lg:top-28 lg:h-fit"
          >
            <div className="mb-2 flex items-center justify-between">
              <ScentFamilyBadge family={perfume.family} />
              <span className="text-xs text-carbon/50">{perfume.concentration}</span>
            </div>

            <h1 className="font-display text-4xl normal-case text-carbon md:text-5xl">
              {perfume.name.toLowerCase()}
            </h1>

            <div className="mt-3 flex items-center gap-2">
              <div className="flex items-center">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={cn(
                      'h-4 w-4',
                      i < Math.round(perfume.rating) ? 'fill-wd-orange text-wd-orange' : 'text-carbon/30'
                    )}
                  />
                ))}
              </div>
              <span className="text-sm text-carbon/60">
                {perfume.rating} ({perfume.reviewCount} {perfume.reviewCount === 1 ? 'review' : 'reviews'})
              </span>
            </div>

            <p className="mt-6 leading-relaxed text-carbon/60">{perfume.description}</p>

            <div className="mt-8">
              <h3 className="mb-3 text-xs uppercase tracking-[0.15em] text-carbon/50">Select size</h3>
              <SizePriceSelector
                perfume={perfume}
                selectedSizeId={selectedSize}
                onSelect={(size) => setSelectedSize(size.id)}
              />
            </div>

            {selectedSizeObj.stockQty !== undefined && (
              <div className="mt-4 flex items-center gap-3 text-sm">
                <span
                  className={cn(
                    'inline-flex h-2 w-2 rounded-full',
                    selectedSizeObj.stockQty > 10
                      ? 'bg-green-500'
                      : selectedSizeObj.stockQty > 0
                        ? 'bg-wd-orange'
                        : 'bg-wd-red'
                  )}
                />
                <span className="text-carbon/70">
                  {selectedSizeObj.stockQty === 0
                    ? 'Out of stock'
                    : selectedSizeObj.stockQty <= 10
                      ? `Only ${selectedSizeObj.stockQty} left`
                      : 'In stock — ships in 1–2 business days'}
                </span>
              </div>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              {!sizeOutOfStock ? (
                <>
                  <Button
                    className="flex-1 bg-wd-orange text-white hover:bg-wd-orange/90"
                    onClick={() => setBuyNowOpen(true)}
                  >
                    Buy now — {formatPrice(selectedSizeObj.price)}
                  </Button>
                  <Button variant="secondary" className="flex-1" onClick={handleAdd}>
                    Add to cart
                  </Button>
                </>
              ) : (
                <Button className="flex-1" disabled>
                  Out of stock
                </Button>
              )}
              {!selectedSizeObj.size.includes('sample') && (
                <Button variant="ghost" onClick={handleSample}>
                  Try first
                </Button>
              )}
              <WishlistHeart
                active={isWishlisted}
                loading={wishlistLoading || !perfume}
                onToggle={handleWishlist}
                burst={wishlistBurst}
                className="h-12 w-12"
              />
            </div>

            <div className="mt-6 space-y-3 border-y border-ink/10 py-5">
              <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] text-carbon/50">
                <MapPin className="h-4 w-4" />
                Check delivery to your city
              </p>
              <div className="flex gap-2">
                <Input
                  placeholder="State e.g. Lagos"
                  value={deliveryState}
                  onChange={(e) => setDeliveryState(e.target.value)}
                  className="flex-1"
                />
                <Input
                  placeholder="City (optional)"
                  value={deliveryCity}
                  onChange={(e) => setDeliveryCity(e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={fetchDeliveryEstimate}
                  isLoading={deliveryLoading}
                >
                  Estimate
                </Button>
              </div>
              {deliveryEstimate && (
                <div className="flex items-start gap-2 text-sm text-carbon/80">
                  <Clock className="h-4 w-4 shrink-0 text-wd-orange" />
                  <span>
                    Delivery: ₦{(deliveryEstimate.base_fee_kobo / 100).toLocaleString()} ·{' '}
                    {deliveryEstimate.min_days}–{deliveryEstimate.max_days} business days
                  </span>
                </div>
              )}
              <p className="flex items-start gap-2 text-xs text-carbon/50">
                <Info className="h-4 w-4 shrink-0" />
                Free shipping on orders over ₦30,000. Samples ship free.
              </p>
            </div>

            <div className="mt-10">
              <Accordion items={accordions} />
            </div>
          </motion.div>
        </div>

        <ReviewsSection slug={perfume.id} rating={perfume.rating} reviewCount={perfume.reviewCount} />

        {related.length > 0 && (
          <div className="mt-24 border-t border-ink/10 pt-16">
            <h2 className="mb-8 font-display text-2xl normal-case text-carbon">Complete the ritual</h2>
            <ProductGrid
              perfumes={related.map((r) => ({
                id: String(r.id),
                name: r.name,
                family: r.family as ScentFamily,
                images: [r.image],
                sizes: [{ id: 'default', size: '', price: r.price, isRefillable: false, inStock: true }],
                description: '',
                story: '',
                notes: { top: [], heart: [], base: [] },
                concentration: 'EDP',
                badges: [],
                rating: 0,
                reviewCount: 0,
              }))}
            />
          </div>
        )}

        {recentlyViewed.length > 1 && (
          <div className="mt-24 border-t border-ink/10 pt-16">
            <h2 className="mb-8 font-display text-2xl normal-case text-carbon">Recently viewed</h2>
            <ProductGrid
              perfumes={recentlyViewed
                .filter((r) => r.slug !== perfume.id)
                .slice(0, 4)
                .map((r) => ({
                  id: r.slug,
                  name: r.name,
                  family: r.family as ScentFamily,
                  images: [r.image],
                  sizes: [{ id: 'default', size: '', price: r.price, isRefillable: false, inStock: true }],
                  description: '',
                  story: '',
                  notes: { top: [], heart: [], base: [] },
                  concentration: 'EDP',
                  badges: [],
                  rating: 0,
                  reviewCount: 0,
                }))}
            />
          </div>
        )}
      </Container>

      <AddToBagConfirmation
        open={addedOpen}
        onClose={() => setAddedOpen(false)}
        perfumeName={perfume.name}
        sizeLabel={selectedSizeObj.size}
        price={selectedSizeObj.price}
        image={perfume.images[0]}
      />

      {buyNowOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4">
          <div ref={buyNowRef} className="relative w-full max-w-md bg-paper p-6 shadow-xl">
            <button
              onClick={() => setBuyNowOpen(false)}
              className="absolute right-4 top-4 text-carbon/50 hover:text-wd-orange"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="mb-2 font-display text-2xl normal-case text-carbon">
              Buy {perfume.name.toLowerCase()}
            </h2>
            <p className="mb-6 text-sm text-carbon/60">
              {selectedSizeObj.size} · {formatPrice(selectedSizeObj.price)}
            </p>
            <form onSubmit={handleBuyNow} className="space-y-4">
              <Input
                label="Email"
                type="email"
                required
                value={buyNowForm.email}
                onChange={(e) => setBuyNowForm((f) => ({ ...f, email: e.target.value }))}
              />
              <Input
                label="Full name"
                value={buyNowForm.name}
                onChange={(e) => setBuyNowForm((f) => ({ ...f, name: e.target.value }))}
              />
              <Input
                label="Phone"
                value={buyNowForm.phone}
                onChange={(e) => setBuyNowForm((f) => ({ ...f, phone: e.target.value }))}
              />
              <Input
                label="Street"
                required
                value={buyNowForm.street}
                onChange={(e) => setBuyNowForm((f) => ({ ...f, street: e.target.value }))}
              />
              <div className="grid gap-4 md:grid-cols-2">
                <Input
                  label="City"
                  required
                  value={buyNowForm.city}
                  onChange={(e) => setBuyNowForm((f) => ({ ...f, city: e.target.value }))}
                />
                <Input
                  label="State"
                  required
                  value={buyNowForm.state}
                  onChange={(e) => setBuyNowForm((f) => ({ ...f, state: e.target.value }))}
                />
              </div>
              <Button type="submit" isLoading={buyNowLoading} className="w-full">
                Pay {formatPrice(selectedSizeObj.price)}
              </Button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
